import {isEmpty} from 'lodash-es';
import React, {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, CheckBox, ConfirmationModal, Input} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import type {UserModalProps} from '@/components/DeactivateAccount/types';
import Modal from '@/components/Modals';
import type {BusinessLimit, BusinessLimitTagMap} from '@/types';
import {BusinessFeeComparator, BusinessLimitField} from '@/types';
import {logRejection} from '@/utils/logRejection';

import LimitInput from './LimitInput';
import {LimitPredicateInput} from './LimitPredicateInput';
import {LimitActionType} from './types';
import useLimitUpdate from './useLimitUpdate';

// Kept in step with the same four rejected by BusinessLimitRequestValidator.
const UNSUPPORTED_LIMIT_FILTER_FIELDS = [
  BusinessLimitField.TransactionCount,
  BusinessLimitField.TransactionVolume,
  BusinessLimitField.TransactionAmount,
  BusinessLimitField.WalletTag,
];

interface LimitFormModalProps extends Pick<
  UserModalProps,
  'isOpen' | 'onClose'
> {
  initialData?: BusinessLimit;
  enforceBusinessTag?: string;
  dataCy?: string;
}

const LimitFormModal = ({
  isOpen,
  onClose,
  initialData,
  enforceBusinessTag,
  dataCy,
}: LimitFormModalProps) => {
  const {dispatch, isLoading, handleSubmit, handleDelete, limit} =
    useLimitUpdate(onClose, initialData);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {name, meta} = limit;
  const flags = meta?.flags ?? {};
  // `LimitUpdateButton` always hands the modal a limit object (for the business
  // tab it injects the enforced BUSINESS tag even when creating), so an empty
  // `initialData` cannot tell a create from an update. An existing record is the
  // one that carries an id, which is also what `useLimitUpdate.handleSubmit`
  // keys the PATCH-vs-POST decision on.
  const isExistingLimit = !isEmpty(initialData?.id);
  const updateLabel = isExistingLimit ? 'Update Limit' : 'Create New Limit +';
  const submitLabel = isExistingLimit ? 'Update' : 'Submit';
  const isDisabled = isEmpty(limit?.name) || isLoading || isEmpty(limit?.limit);

  const handleConfirmDelete = async () => {
    await handleDelete(String(limit.id));
    setShowDeleteConfirm(false);
  };
  return (
    <React.Fragment>
      <Modal
        dataCy={dataCy}
        isOpen={isOpen}
        onClose={onClose}
        width="w-11/12 sm:w-5/12 md:w-5/12 lg:w-5/12 xl:w-3/12 px-4 sm:px-0"
        closeOnOutsideClick
        title={updateLabel}
      >
        <div className="pt-3 px-2 overflow-hidden">
          {/* The form stays mounted while saving (as SettlementAccountFormModal
              does), so a failed save keeps every value the admin entered. */}
          <form
            aria-busy={isLoading}
            onSubmit={(e) => {
              e.preventDefault();
              const limitToSubmit = enforceBusinessTag
                ? {
                    ...limit,
                    meta: {
                      ...limit.meta,
                      tags: {
                        ...limit.meta?.tags,
                        // Business tags follow the shared `COMPARATOR::value`
                        // shape the server validator enforces on every tag.
                        [BusinessLimitField.Business]: `${BusinessFeeComparator.Equals}::${enforceBusinessTag}`,
                      },
                    },
                  }
                : limit;
              // `handleSubmit` toasts its own failures, so this only fires if
              // that handler itself throws. Log rather than swallow silently.
              handleSubmit(limitToSubmit).catch(
                logRejection('Failed to submit limit'),
              );
            }}
            className="flex flex-col"
          >
            <Input
              label="Name"
              data-cy={dataCy ? `${dataCy}-name-input` : undefined}
              placeholder="Name of Limit"
              value={name || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                dispatch({
                  type: LimitActionType.UPDATE_LIMIT,
                  payload: {
                    name: e.target?.value,
                  },
                })
              }
              className="w-full mb-2"
            />

            <div className="mb-2">
              <div className="w-full mb-2">
                <LimitPredicateInput
                  dataCy={dataCy}
                  tags={meta?.tags}
                  onRemove={(tag: BusinessLimitField) =>
                    dispatch({
                      type: LimitActionType.REMOVE_TAG,
                      payload: tag,
                    })
                  }
                  onChange={(tag: BusinessLimitTagMap) =>
                    dispatch({
                      type: LimitActionType.ADD_TAG,
                      payload: tag,
                    })
                  }
                  // Transactions are never tagged with these four (see
                  // generateBusinessLimitTags), so a limit filtered on one
                  // of them cannot apply as written and the API now rejects
                  // it. They stay listed on limits that already carry them
                  // so those can be removed and the limit saved.
                  fieldsToOmit={UNSUPPORTED_LIMIT_FILTER_FIELDS}
                  lockedFields={
                    enforceBusinessTag
                      ? [BusinessLimitField.Business]
                      : undefined
                  }
                />
              </div>
            </div>

            <LimitInput
              dataCy={dataCy}
              label="Limits"
              value={limit?.limit}
              onChange={(map: BusinessLimit['limit']) =>
                dispatch({
                  type: LimitActionType.SET_LIMIT_MAP,
                  payload: map,
                })
              }
            />
            <hr className="my-4 dark:border-dark-borderMid" />

            <CheckBox
              label="Active"
              type="checkbox"
              dataCy={dataCy ? `${dataCy}-active` : undefined}
              checked={Boolean(!flags?.isDeactivated)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                dispatch({
                  type: LimitActionType.UPDATE_LIMIT,
                  payload: {
                    meta: {
                      ...limit.meta,
                      flags: {
                        ...flags,
                        isDeactivated: !e.target?.checked,
                      },
                    },
                  },
                })
              }
            />

            <div className="flex items-center justify-end pb-5">
              {isExistingLimit && (
                <PermissionsProvider
                  permission={AfriexAdminPermission.BusinessLimitManage}
                >
                  <Button
                    dataCy={dataCy ? `${dataCy}-delete` : undefined}
                    variant="outline"
                    colorScheme="red"
                    className="mr-auto"
                    disabled={isLoading}
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete
                  </Button>
                </PermissionsProvider>
              )}
              {isLoading && (
                <span
                  role="status"
                  className="mr-3 text-sm text-gray-500 dark:text-gray-400"
                >
                  Saving...
                </span>
              )}
              <Button
                dataCy={dataCy ? `${dataCy}-submit` : undefined}
                disabled={isDisabled}
                type="submit"
                variant="solid"
                colorScheme="cyan"
              >
                {submitLabel}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        dataCy={dataCy ? `${dataCy}-delete-confirm` : undefined}
        title="Delete Limit"
        label={`Delete limit ${name ?? ''}? This cannot be undone.`}
        confirmActionLabel="Delete"
        cancelActionLabel="Cancel"
        loadingLabel="Deleting..."
        loading={isLoading}
        confirmAction={handleConfirmDelete}
        cancelAction={() => setShowDeleteConfirm(false)}
      />
    </React.Fragment>
  );
};

export default LimitFormModal;
